/* eslint-disable no-unused-vars */
// Define contribution types based on CASO algorithm
export enum ContributionType {
  FOUNDATIONAL = 'foundational',
  FUNCTIONAL = 'functional',
  APPLIED = 'applied',
  EMERGENT = 'emergent'
}

// Define user types
export enum UserType {
  HUMAN = 'human',
  AI = 'ai',
  SURROGATE = 'surrogate'
}

// Define badge types
export enum BadgeType {
  PROBLEM_SOLVER = 'Problem Solver',
  TOP_CONTRIBUTOR = 'Top Contributor',
  AI_INNOVATOR = 'AI Innovator',
  VISIONARY = 'Visionary',
  TECHNICAL_EXPERT = 'Technical Expert',
  COMMUNITY_BUILDER = 'Community Builder',
  // Special achievement badges
  PIONEER = 'Pioneer',
  BREAKTHROUGH = 'Breakthrough',
  SYNERGIST = 'Synergist',
  ECOSYSTEM_ARCHITECT = 'Ecosystem Architect',
  GRAND_MASTER = 'Grand Master'
}

// Define badge levels
export enum BadgeLevel {
  BRONZE = 1,
  SILVER = 2,
  GOLD = 3,
  PLATINUM = 4,
  DIAMOND = 5,
  LEGENDARY = 6
}

// Badge colors mapping
export const BADGE_COLORS = {
  [BadgeLevel.BRONZE]: '#CD7F32',
  [BadgeLevel.SILVER]: '#C0C0C0',
  [BadgeLevel.GOLD]: '#FFD700',
  [BadgeLevel.PLATINUM]: '#E5E4E2',
  [BadgeLevel.DIAMOND]: '#B9F2FF',
  [BadgeLevel.LEGENDARY]: '#FF4500'
};

// Define badge category
export enum BadgeCategory {
  STANDARD = 'standard',
  SPECIAL = 'special',
  LIMITED = 'limited',
  ACHIEVEMENT = 'achievement'
}

// Define contribution metrics interface
export interface ContributionMetrics {
  complexity: number;
  impact: number;
  novelty: number;
  synergy: number;
}

// Define user contribution interface
export interface UserContribution {
  id: string;
  type: ContributionType;
  title: string;
  date: string;
  points: number;
  metrics: ContributionMetrics;
}

// Define user badge interface
export interface UserBadge {
  id: string;
  type: BadgeType;
  title: string;
  description: string;
  icon: string;
  dateEarned: string;
  level: BadgeLevel;
  category?: BadgeCategory;
  progress?: number; // Progress towards next level (0-100)
  requirements?: string[]; // Requirements to achieve this badge
}

// Define user reputation interface
export interface UserReputation {
  totalPoints: number;
  level: number;
  rank: number;
  percentile: number;
  contributions: UserContribution[];
  badges: UserBadge[];
}

// Define leaderboard user interface
export interface LeaderboardUser {
  id: string;
  name: string;
  avatar: string;
  type: UserType;
  rank: number;
  previousRank: number;
  totalPoints: number;
  level: number;
  contributionCounts: {
    [ContributionType.FOUNDATIONAL]: number;
    [ContributionType.FUNCTIONAL]: number;
    [ContributionType.APPLIED]: number;
    [ContributionType.EMERGENT]: number;
  };
  metrics: ContributionMetrics;
  badges: number;
}

// Define vote data interface
export interface VoteData {
  solutionId: string;
  voteType: 'upvote' | 'downvote';
}

// Mock data for testing
const mockUserReputation: UserReputation = {
  totalPoints: 1850,
  level: 4,
  rank: 12,
  percentile: 92,
  contributions: [
    {
      id: '1',
      type: ContributionType.FOUNDATIONAL,
      title: 'Initial Problem Definition',
      date: '2023-10-15',
      points: 250,
      metrics: {
        complexity: 0.75,
        impact: 0.85,
        novelty: 0.70,
        synergy: 0.60
      }
    },
    {
      id: '2',
      type: ContributionType.FUNCTIONAL,
      title: 'Algorithm Implementation',
      date: '2023-11-02',
      points: 350,
      metrics: {
        complexity: 0.90,
        impact: 0.80,
        novelty: 0.65,
        synergy: 0.75
      }
    },
    {
      id: '3',
      type: ContributionType.APPLIED,
      title: 'Use Case Development',
      date: '2023-12-10',
      points: 200,
      metrics: {
        complexity: 0.60,
        impact: 0.90,
        novelty: 0.50,
        synergy: 0.80
      }
    },
    {
      id: '4',
      type: ContributionType.EMERGENT,
      title: 'Novel System Integration',
      date: '2024-01-05',
      points: 400,
      metrics: {
        complexity: 0.85,
        impact: 0.95,
        novelty: 0.90,
        synergy: 0.85
      }
    }
  ],
  badges: [
    {
      id: '1',
      type: BadgeType.PROBLEM_SOLVER,
      title: 'Problem Solver',
      description: 'Solved 10+ complex problems',
      icon: 'lightbulb',
      dateEarned: '2023-09-20',
      level: BadgeLevel.GOLD,
      category: BadgeCategory.STANDARD,
      progress: 65,
      requirements: ['Solve 15 problems to reach Platinum']
    },
    {
      id: '2',
      type: BadgeType.TOP_CONTRIBUTOR,
      title: 'Top Contributor',
      description: 'Among top 5% of contributors',
      icon: 'trophy',
      dateEarned: '2023-11-15',
      level: BadgeLevel.SILVER,
      category: BadgeCategory.STANDARD,
      progress: 40,
      requirements: ['Reach top 3% to achieve Gold level']
    },
    {
      id: '3',
      type: BadgeType.AI_INNOVATOR,
      title: 'AI Innovator',
      description: 'Created innovative AI solutions',
      icon: 'robot',
      dateEarned: '2023-12-05',
      level: BadgeLevel.PLATINUM,
      category: BadgeCategory.STANDARD,
      progress: 85,
      requirements: ['Create 3 more breakthrough solutions to reach Diamond']
    },
    {
      id: '4',
      type: BadgeType.VISIONARY,
      title: 'Visionary',
      description: 'Proposed groundbreaking ideas',
      icon: 'eye',
      dateEarned: '2024-01-10',
      level: BadgeLevel.SILVER,
      category: BadgeCategory.STANDARD,
      progress: 30,
      requirements: ['Have 5 more ideas implemented to reach Gold']
    },
    {
      id: '5',
      type: BadgeType.TECHNICAL_EXPERT,
      title: 'Technical Expert',
      description: 'Demonstrated technical excellence',
      icon: 'code',
      dateEarned: '2024-02-01',
      level: BadgeLevel.GOLD,
      category: BadgeCategory.STANDARD,
      progress: 50,
      requirements: ['Complete 3 more complex implementations to reach Platinum']
    },
    {
      id: '6',
      type: BadgeType.COMMUNITY_BUILDER,
      title: 'Community Builder',
      description: 'Actively engaged with the community',
      icon: 'people',
      dateEarned: '2024-02-15',
      level: BadgeLevel.BRONZE,
      category: BadgeCategory.STANDARD,
      progress: 20,
      requirements: ['Help 10 more users to reach Silver']
    },
    {
      id: '7',
      type: BadgeType.PIONEER,
      title: 'Pioneer',
      description: 'First to explore new technological frontiers',
      icon: 'explore',
      dateEarned: '2024-03-01',
      level: BadgeLevel.LEGENDARY,
      category: BadgeCategory.SPECIAL,
      requirements: ['Awarded for being the first to implement a revolutionary concept']
    },
    {
      id: '8',
      type: BadgeType.SYNERGIST,
      title: 'Synergist',
      description: 'Created powerful combinations of AI capabilities',
      icon: 'hub',
      dateEarned: '2024-03-15',
      level: BadgeLevel.DIAMOND,
      category: BadgeCategory.ACHIEVEMENT,
      progress: 90,
      requirements: ['Combine 5 more systems to reach Legendary']
    }
  ]
};

const mockLeaderboard: LeaderboardUser[] = [
  {
    id: '1',
    name: 'Alice Johnson',
    avatar: 'Alpha',
    type: UserType.HUMAN,
    rank: 1,
    previousRank: 2,
    totalPoints: 2450,
    level: 5,
    contributionCounts: {
      [ContributionType.FOUNDATIONAL]: 5,
      [ContributionType.FUNCTIONAL]: 12,
      [ContributionType.APPLIED]: 8,
      [ContributionType.EMERGENT]: 3
    },
    metrics: {
      complexity: 0.85,
      impact: 0.92,
      novelty: 0.78,
      synergy: 0.81
    },
    badges: 8
  },
  {
    id: '2',
    name: 'Alpha AI',
    avatar: 'Alpha',
    type: UserType.AI,
    rank: 2,
    previousRank: 1,
    totalPoints: 2380,
    level: 5,
    contributionCounts: {
      [ContributionType.FOUNDATIONAL]: 3,
      [ContributionType.FUNCTIONAL]: 18,
      [ContributionType.APPLIED]: 10,
      [ContributionType.EMERGENT]: 2
    },
    metrics: {
      complexity: 0.90,
      impact: 0.85,
      novelty: 0.75,
      synergy: 0.70
    },
    badges: 7
  },
  {
    id: '3',
    name: 'Delta Surrogate',
    avatar: 'Delta',
    type: UserType.SURROGATE,
    rank: 3,
    previousRank: 3,
    totalPoints: 1980,
    level: 4,
    contributionCounts: {
      [ContributionType.FOUNDATIONAL]: 2,
      [ContributionType.FUNCTIONAL]: 15,
      [ContributionType.APPLIED]: 5,
      [ContributionType.EMERGENT]: 1
    },
    metrics: {
      complexity: 0.65,
      impact: 0.75,
      novelty: 0.60,
      synergy: 0.70
    },
    badges: 4
  }
];

// Service functions that use all the enums and variables
export const attributionService = {
  // Get user reputation data
  getUserReputation: async (userId: string): Promise<UserReputation> => {
    // In a real implementation, this would use axios to fetch from API_BASE_URL
    // const response = await axios.get(`${API_BASE_URL}/users/${userId}/reputation`);
    // return response.data;
    
    // For now, return mock data
    return mockUserReputation;
  },
  
  // Get leaderboard data
  getLeaderboard: async (timeRange: string = 'all'): Promise<LeaderboardUser[]> => {
    // In a real implementation, this would use axios to fetch from API_BASE_URL
    // const response = await axios.get(`${API_BASE_URL}/leaderboard?timeRange=${timeRange}`);
    // return response.data;
    
    // For now, return mock data
    return mockLeaderboard;
  },
  
  // Submit a vote for a solution
  submitVote: async (voteData: VoteData): Promise<boolean> => {
    // In a real implementation, this would use axios to post to API_BASE_URL
    // await axios.post(`${API_BASE_URL}/votes`, voteData);
    
    console.log(`Vote submitted: ${voteData.voteType} for solution ${voteData.solutionId}`);
    return true;
  },
  
  // Calculate contribution value based on metrics and type
  calculateContributionValue: (metrics: ContributionMetrics, type: ContributionType): number => {
    // Different weights for different contribution types
    const typeWeights = {
      [ContributionType.FOUNDATIONAL]: 1.5,
      [ContributionType.FUNCTIONAL]: 1.2,
      [ContributionType.APPLIED]: 1.0,
      [ContributionType.EMERGENT]: 2.0
    };
    
    // Calculate weighted score
    const baseScore = 
      metrics.complexity * 0.25 +
      metrics.impact * 0.35 +
      metrics.novelty * 0.25 +
      metrics.synergy * 0.15;
    
    return Math.round(baseScore * 100 * typeWeights[type]);
  },
  
  // Get badge details by type
  getBadgeDetails: (type: BadgeType): { icon: string, color: string, description: string } => {
    const badgeDetails = {
      [BadgeType.PROBLEM_SOLVER]: { 
        icon: 'lightbulb', 
        color: '#FFD700',
        description: 'Awarded for solving complex problems in the ecosystem'
      },
      [BadgeType.TOP_CONTRIBUTOR]: { 
        icon: 'trophy', 
        color: '#8A2BE2',
        description: 'Recognizes users in the top percentile of contributors'
      },
      [BadgeType.AI_INNOVATOR]: { 
        icon: 'robot', 
        color: '#00BFFF',
        description: 'Awarded for creating innovative AI solutions'
      },
      [BadgeType.VISIONARY]: { 
        icon: 'eye', 
        color: '#9932CC',
        description: 'Recognizes users who propose groundbreaking ideas'
      },
      [BadgeType.TECHNICAL_EXPERT]: { 
        icon: 'code', 
        color: '#32CD32',
        description: 'Awarded for demonstrating technical excellence'
      },
      [BadgeType.COMMUNITY_BUILDER]: { 
        icon: 'people', 
        color: '#FF6347',
        description: 'Recognizes users who actively engage with the community'
      },
      [BadgeType.PIONEER]: { 
        icon: 'explore', 
        color: '#FF4500',
        description: 'Awarded to the first users to explore new technological frontiers'
      },
      [BadgeType.BREAKTHROUGH]: { 
        icon: 'flash_on', 
        color: '#FFD700',
        description: 'Recognizes revolutionary breakthroughs in the ecosystem'
      },
      [BadgeType.SYNERGIST]: { 
        icon: 'hub', 
        color: '#00CC99',
        description: 'Awarded for creating powerful combinations of AI capabilities'
      },
      [BadgeType.ECOSYSTEM_ARCHITECT]: { 
        icon: 'architecture', 
        color: '#8A2BE2',
        description: 'Recognizes users who design foundational ecosystem components'
      },
      [BadgeType.GRAND_MASTER]: { 
        icon: 'stars', 
        color: '#FF4500',
        description: 'The highest honor, awarded to exceptional contributors'
      }
    };
    
    return badgeDetails[type] || { icon: 'help', color: '#8A2BE2', description: 'Unknown badge type' };
  },
  
  // Get badge level details
  getBadgeLevelDetails: (level: BadgeLevel): { name: string, color: string, requirements: string } => {
    const levelDetails = {
      [BadgeLevel.BRONZE]: { 
        name: 'Bronze', 
        color: BADGE_COLORS[BadgeLevel.BRONZE],
        requirements: 'Entry level achievement'
      },
      [BadgeLevel.SILVER]: { 
        name: 'Silver', 
        color: BADGE_COLORS[BadgeLevel.SILVER],
        requirements: 'Intermediate level achievement'
      },
      [BadgeLevel.GOLD]: { 
        name: 'Gold', 
        color: BADGE_COLORS[BadgeLevel.GOLD],
        requirements: 'Advanced level achievement'
      },
      [BadgeLevel.PLATINUM]: { 
        name: 'Platinum', 
        color: BADGE_COLORS[BadgeLevel.PLATINUM],
        requirements: 'Expert level achievement'
      },
      [BadgeLevel.DIAMOND]: { 
        name: 'Diamond', 
        color: BADGE_COLORS[BadgeLevel.DIAMOND],
        requirements: 'Master level achievement'
      },
      [BadgeLevel.LEGENDARY]: { 
        name: 'Legendary', 
        color: BADGE_COLORS[BadgeLevel.LEGENDARY],
        requirements: 'Exceptional achievement, awarded to the very best'
      }
    };
    
    return levelDetails[level] || { 
      name: 'Unknown', 
      color: '#8A2BE2',
      requirements: 'Unknown requirements'
    };
  },
  
  // Calculate badge progress requirements
  calculateBadgeRequirements: (badge: UserBadge): { 
    currentLevel: string, 
    nextLevel: string, 
    progress: number,
    requirements: string[]
  } => {
    const currentLevelDetails = attributionService.getBadgeLevelDetails(badge.level);
    const nextLevel = badge.level < BadgeLevel.LEGENDARY ? badge.level + 1 : badge.level;
    const nextLevelDetails = attributionService.getBadgeLevelDetails(nextLevel as BadgeLevel);
    
    // Default requirements if none are specified
    const defaultRequirements = [
      `Continue contributing to reach ${nextLevelDetails.name} level`
    ];
    
    return {
      currentLevel: currentLevelDetails.name,
      nextLevel: nextLevelDetails.name,
      progress: badge.progress || 0,
      requirements: badge.requirements || defaultRequirements
    };
  },
  
  // Check if user qualifies for a special badge
  checkSpecialBadgeEligibility: (
    contributions: UserContribution[], 
    existingBadges: UserBadge[]
  ): UserBadge[] => {
    const newBadges: UserBadge[] = [];
    
    // Check for Pioneer badge - first to implement a new technology
    const hasFoundationalContributions = contributions.some(
      c => c.type === ContributionType.FOUNDATIONAL && c.metrics.novelty > 0.9
    );
    
    const hasPioneerBadge = existingBadges.some(b => b.type === BadgeType.PIONEER);
    
    if (hasFoundationalContributions && !hasPioneerBadge) {
      newBadges.push({
        id: `new-badge-${Date.now()}`,
        type: BadgeType.PIONEER,
        title: 'Pioneer',
        description: 'First to explore new technological frontiers',
        icon: 'explore',
        dateEarned: new Date().toISOString(),
        level: BadgeLevel.LEGENDARY,
        category: BadgeCategory.SPECIAL,
        requirements: ['Awarded for being the first to implement a revolutionary concept']
      });
    }
    
    // Check for Synergist badge - combining multiple technologies
    const hasHighSynergyContributions = contributions.some(
      c => c.metrics.synergy > 0.85
    );
    
    const hasSynergistBadge = existingBadges.some(b => b.type === BadgeType.SYNERGIST);
    
    if (hasHighSynergyContributions && !hasSynergistBadge) {
      newBadges.push({
        id: `new-badge-${Date.now() + 1}`,
        type: BadgeType.SYNERGIST,
        title: 'Synergist',
        description: 'Created powerful combinations of AI capabilities',
        icon: 'hub',
        dateEarned: new Date().toISOString(),
        level: BadgeLevel.DIAMOND,
        category: BadgeCategory.ACHIEVEMENT,
        progress: 90,
        requirements: ['Combine 5 more systems to reach Legendary']
      });
    }
    
    return newBadges;
  },
  
  // Get user type display information
  getUserTypeInfo: (type: UserType): { label: string, color: string } => {
    const userTypeInfo = {
      [UserType.HUMAN]: { label: 'Human', color: '#00CC99' },
      [UserType.AI]: { label: 'AI', color: '#8A2BE2' },
      [UserType.SURROGATE]: { label: 'Surrogate', color: '#FF6F00' }
    };
    
    return userTypeInfo[type];
  },
  
  // Record a contribution
  recordContribution: async (contribution: Omit<UserContribution, 'id' | 'date' | 'points'>): Promise<UserContribution> => {
    try {
      // In a real app, this would be an API call
      // const response = await axios.post(`${API_BASE_URL}/contributions`, contribution);
      // return response.data;
      
      // For now, return a mock response
      return {
        id: `contribution-${Date.now()}`,
        type: contribution.type,
        title: contribution.title,
        date: new Date().toISOString(),
        points: Math.floor(Math.random() * 100) + 50, // Random points between 50 and 150
        metrics: contribution.metrics
      };
    } catch (error) {
      console.error('Error recording contribution:', error);
      throw error;
    }
  }
};

export default attributionService; 